'use strict';

Object.defineProperty(exports, '__esModule', { value: true });

/**
 * Default color configuration object.
 * @type {colors} defaultColors
 * @property {string} white #ffffff
 * @property {string} black #000000
 */
var defaultColors = {
  white: '#ffffff',
  black: '#000000'
};

/**
 * Composable CSS property.
 * @typedef {Object<string, string|Object<string, string>>} utility
 * @example
 * { display: 'block' }
 * // or
 * { ':hover': { color: 'red' } }
 */

/**
 * Composable utiltiy objects.
 * @typedef {Object<string, utility>} layoutUtilities
 */
var utilities = {
  /**
   * @memberof layoutUtilities
   * @property
   * @return {utility} { display: 'block' }
   */
  block: { display: 'block' },
  /**
   * @memberof layoutUtilities
   * @property
   * @return {utility} { display: 'inline-block' }
   */
  inlineBlock: { display: 'inline-block' },
  /**
   * @memberof layoutUtilities
   * @property
   * @return {utility} { display: 'flex' }
   */
  flex: { display: 'flex' },
  /**
   * @memberof layoutUtilities
   * @property
   * @return {utility} { flex: 0.5, display: 'flex' }
   */
  flexHalf: { flex: 0.5, display: 'flex' },
  /**
   * @memberof layoutUtilities
   * @property
   * @return {utility} { flexDirection: 'row' }
   */
  flexRow: { flexDirection: 'row' },
  /**
   * @memberof layoutUtilities
   * @property
   * @return {utility} { flexDirection: 'column' }
   */
  flexColumn: { flexDirection: 'column' },
  /**
   * @memberof layoutUtilities
   * @property
   * @return {utility} { flexDirection: 'row-reverse' }
   */
  rowReverse: { flexDirection: 'row-reverse' },
  /**
   * @memberof layoutUtilities
   * @property
   * @return {utility} { flexDirection: 'column-reverse' }
   */
  columnReverse: { flexDirection: 'column-reverse' },
  /**
   * @memberof layoutUtilities
   * @property
   * @return {utility} { flexWrap: 'nowrap' }
   */
  noWrap: { flexWrap: 'nowrap' },
  /**
   * @memberof layoutUtilities
   * @property
   * @return {utility} { flexWrap: 'wrap' }
   */
  wrap: { flexWrap: 'wrap' },
  /**
   * @memberof layoutUtilities
   * @property
   * @return {utility} { justifyContent: 'flex-start' }
   */
  justifyStart: { justifyContent: 'flex-start' },
  /**
   * @memberof layoutUtilities
   * @property
   * @return {utility} { justifyContent: 'flex-end' }
   */
  justifyEnd: { justifyContent: 'flex-end' },
  /**
   * @memberof layoutUtilities
   * @property
   * @return {utility} { justifyContent: 'center' }
   */
  justifyCenter: { justifyContent: 'center' },
  /**
   * @memberof layoutUtilities
   * @property
   * @return {utility} { justifyContent: 'space-between' }
   */
  spaceBetween: { justifyContent: 'space-between' },
  /**
   * @memberof layoutUtilities
   * @property
   * @return {utility} { justifyContent: 'space-around' }
   */
  spaceAround: { justifyContent: 'space-around' },
  /**
   * @memberof layoutUtilities
   * @property
   * @return {utility} { alignItems: 'flex-start' }
   */
  alignStart: { alignItems: 'flex-start' },
  /**
   * @memberof layoutUtilities
   * @property
   * @return {utility} { alignItems: 'flex-end' }
   */
  alignEnd: { alignItems: 'flex-end' },
  /**
   * @memberof layoutUtilities
   * @property
   * @return {utility} { alignItems: 'center' }
   */
  alignCenter: { alignItems: 'center' },
  /**
   * @memberof layoutUtilities
   * @property
   * @return {utility} { alignItems: 'baseline' }
   */
  alignBase: { alignItems: 'baseline' },
  /**
   * @memberof layoutUtilities
   * @property
   * @return {utility} { alignItems: 'stretch' }
   */
  alignStretch: { alignItems: 'stretch' },
  /**
   * @memberof layoutUtilities
   * @property
   * @return {utility} { alignSelf: 'flex-start' }
   */
  selfStart: { alignSelf: 'flex-start' },
  /**
   * @memberof layoutUtilities
   * @property
   * @return {utility} { alignSelf: 'flex-end' }
   */
  selfEnd: { alignSelf: 'flex-end' },
  /**
   * @memberof layoutUtilities
   * @property
   * @return {utility} { alignSelf: 'center' }
   */
  selfCenter: { alignSelf: 'center' },
  /**
   * @memberof layoutUtilities
   * @property
   * @return {utility} { alignSelf: 'baseline' }
   */
  selfBase: { alignSelf: 'baseline' },
  /**
   * @memberof layoutUtilities
   * @property
   * @return {utility} { alignSelf: 'stretch' }
   */
  selfStretch: { alignSelf: 'stretch' },
  /**
   * @memberof layoutUtilities
   * @property
   * @return {utility} { flexGrow: 0 }
   */
  grow0: { flexGrow: 0 },
  /**
   * @memberof layoutUtilities
   * @property
   * @return {utility} { flexGrow: 1 }
   */
  grow1: { flexGrow: 1 },
  /**
   * @memberof layoutUtilities
   * @property
   * @return {utility} { flexShrink: 0 }
   */
  shrink0: { flexShrink: 0 },
  /**
   * @memberof layoutUtilities
   * @property
   * @return {utility} { flexShrink: 1 }
   */
  shrink1: { flexShrink: 1 },
  /**
   * @memberof layoutUtilities
   * @property
   * @return {utility} { overflow: 'visible' }
   */
  overflowVisible: { overflow: 'visible' },
  /**
   * @memberof layoutUtilities
   * @property
   * @return {utility} { overflow: 'scroll' }
   */
  overflowScroll: { overflow: 'scroll' },
  /**
   * @memberof layoutUtilities
   * @property
   * @return {utility} { top: 0 }
   */
  t0: { top: 0 },
  /**
   * @memberof layoutUtilities
   * @property
   * @return {utility} { left: 0 }
   */
  l0: { left: 0 },
  /**
   * @memberof layoutUtilities
   * @property
   * @return {utility} { right: 0 }
   */
  r0: { right: 0 },
  /**
   * @memberof layoutUtilities
   * @property
   * @return {utility} { bottom: 0 }
   */
  b0: { bottom: 0 },
  /**
   * @memberof layoutUtilities
   * @property
   * @return {utility} { position: 'relative' }
   */
  relative: { position: 'relative' },
  /**
   * @memberof layoutUtilities
   * @property
   * @return {utility} { position: 'fixed' }
   */
  fixed: { position: 'fixed' },
  /**
   * @memberof layoutUtilities
   * @property
   * @return {utility} { position: 'absolute' }
   */
  absolute: { position: 'absolute' }
};

var defineProperty = function (obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
};

var _extends = Object.assign || function (target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i];

    for (var key in source) {
      if (Object.prototype.hasOwnProperty.call(source, key)) {
        target[key] = source[key];
      }
    }
  }

  return target;
};

var capitalize = function capitalize(s) {
  return s.charAt(0).toUpperCase() + s.slice(1);
};

/**
 * Composable color utilities
 * @typedef {Object<string, utility>} colorUtilities
 * @example
 * {
 *  red: { color: 'red' },
 *  hoverRed: { ':hover': { color: 'red' } },
 *  bgRed: { backgroundColor: 'red' },
 *  borderRed: { borderColor: 'red' },
 *  strokeRed: { stroke: 'red' },
 *  fillRed: { fill: 'red' },
 * }
 */

var generateColorUtilities = function generateColorUtilities(colors) {
  return Object.keys(colors).reduce(function (a, b) {
    var _babelHelpers$extends;

    return _extends({}, a, (_babelHelpers$extends = {}, defineProperty(_babelHelpers$extends, b, { color: colors[b] }), defineProperty(_babelHelpers$extends, 'hover' + capitalize(b), { ':hover': { color: colors[b] } }), defineProperty(_babelHelpers$extends, 'bg' + capitalize(b), { backgroundColor: colors[b] }), defineProperty(_babelHelpers$extends, 'border' + capitalize(b), { borderColor: colors[b] }), defineProperty(_babelHelpers$extends, 'stroke' + capitalize(b), { stroke: colors[b] }), defineProperty(_babelHelpers$extends, 'fill' + capitalize(b), { fill: colors[b] }), _babelHelpers$extends));
  }, {});
};

var allColorUtilities = function allColorUtilities(colors) {
  return _extends({}, generateColorUtilities(defaultColors), generateColorUtilities(colors));
};

/**
 * An optional color configuration object
 * @constant colors
 * @typedef colors
 * @type {Object<string, string>}
 * @example
 * { red: '#EE3124', redTint: '#F9C1BD' }
 */

/**
 * Optional function to generate utilities with custom colors.
 * @namespace
 * @function
 * @type {function}
 * @memberof cooltilities
 * @param  {[colors]} [colors=defaultColors]
 * @return {cooltilities} All composable utility objects with custom color utilities.
 */
var generate = function generate() {
  var colors = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : defaultColors;

  return _extends({}, allColorUtilities(colors), utilities);
};

/**
 * Composable utility objects for css-in-js libraries.
 * @namespace
 * @constant {Object<string, utility>}
 * @return {Object<string, utility>} All composable utility objects including {@link #layoututilties} and {@link #colorutilties}.
 */
var cooltilities = generate();
cooltilities.generate = generate;

exports.generate = generate;
exports.default = cooltilities;



//////////////////
// WEBPACK FOOTER
// ./node_modules/cooltilities/dist/cooltilities.js
// module id = 374
// module chunks = 0